/* Shared by image/text badges and product highlight icons. No Alpine dependency. */
(() => {
  if (customElements.get('badge-explanation')) return;
  let nextId = 0;
  class BadgeExplanation extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected || this.controller) return;
        const text = this.dataset.tooltip?.trim();
        if (!text) return;
        this.controller = new AbortController();
        const on = (target, event, handler, options = {}) => target.addEventListener(event, handler, { ...options, signal: this.controller.signal });
        this.trigger = document.createElement('button');
        this.trigger.type = 'button';
        this.trigger.className = 'badge-explanation__trigger';
        this.trigger.setAttribute('aria-label', this.dataset.label || this.textContent.trim() || text);
        this.trigger.append(...this.childNodes);
        this.append(this.trigger);
        this.tip = document.createElement('div');
        this.tip.id = `badge-explanation-${++nextId}`;
        this.tip.className = 'badge-explanation__tooltip';
        this.tip.role = 'tooltip';
        const content = document.createElement('span');
        content.className = 'badge-explanation__content';
        content.textContent = text;
        this.tip.append(content);
        this.tip.hidden = true;
        document.body.append(this.tip);
        this.trigger.setAttribute('aria-describedby', this.tip.id);
        this.trigger.setAttribute('aria-expanded', 'false');
        on(this.trigger, 'pointerenter', event => { if (event.pointerType !== 'touch') this.open(); });
        on(this.trigger, 'pointerleave', () => this.scheduleClose());
        on(this.tip, 'pointerenter', () => clearTimeout(this.closeTimer));
        on(this.tip, 'pointerleave', () => this.scheduleClose());
        on(this.trigger, 'focus', () => { if (!this.pointerFocus) this.open(); });
        on(this.trigger, 'blur', () => this.close());
        on(this.trigger, 'pointerdown', event => { this.pointerFocus = true; event.stopPropagation(); });
        on(this.trigger, 'click', event => {
          event.preventDefault();
          event.stopPropagation();
          this.isOpen ? this.close() : this.open();
          this.pointerFocus = false;
        });
        on(document, 'pointerdown', event => { if (!this.contains(event.target) && !this.tip.contains(event.target)) this.close(); }, { capture: true });
        on(document, 'keydown', event => { if (event.key === 'Escape' && this.isOpen) { this.close(); event.stopPropagation(); } }, { capture: true });
        on(window, 'resize', () => this.position());
        on(document, 'scroll', () => this.position(), { capture: true, passive: true });
        if (window.visualViewport) {
          on(window.visualViewport, 'resize', () => this.position());
          on(window.visualViewport, 'scroll', () => this.position());
        }
      });
    }
    open() {
      clearTimeout(this.closeTimer);
      clearTimeout(this.hideTimer);
      cancelAnimationFrame(this.openFrame);
      this.isOpen = true;
      this.tip.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
      this.position();
      this.openFrame = requestAnimationFrame(() => {
        if (this.isOpen) this.tip.classList.add('is-open');
      });
    }
    close() {
      clearTimeout(this.closeTimer);
      if (!this.isOpen) return;
      this.isOpen = false;
      cancelAnimationFrame(this.openFrame);
      this.tip.classList.remove('is-open');
      this.hideTimer = setTimeout(() => { this.tip.hidden = true; }, 180);
      this.trigger.setAttribute('aria-expanded', 'false');
    }
    scheduleClose() {
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => { if (document.activeElement !== this.trigger) this.close(); }, 150);
    }
    position() {
      if (!this.isOpen) return;
      const rect = this.trigger.getBoundingClientRect();
      const viewport = window.visualViewport;
      const viewportLeft = viewport?.offsetLeft || 0;
      const viewportTop = viewport?.offsetTop || 0;
      const right = viewportLeft + (viewport?.width || document.documentElement.clientWidth);
      const bottom = viewportTop + (viewport?.height || window.innerHeight);
      const tip = this.tip.getBoundingClientRect();
      const roomAbove = rect.top - viewportTop - 20;
      const roomBelow = bottom - rect.bottom - 20;
      const above = tip.height <= roomAbove || roomAbove >= roomBelow;
      const left = Math.max(viewportLeft + 8, Math.min(rect.left + (rect.width - tip.width) / 2, right - tip.width - 8));
      const top = above ? rect.top - tip.height - 12 : rect.bottom + 12;
      this.tip.classList.toggle('badge-explanation__tooltip--top', above);
      this.tip.classList.toggle('badge-explanation__tooltip--bottom', !above);
      this.tip.style.left = `${left}px`;
      this.tip.style.top = `${Math.max(viewportTop + 8, Math.min(top, bottom - tip.height - 8))}px`;
      const caretLeft = Math.max(10, Math.min(rect.left + rect.width / 2 - left, Math.max(10, tip.width - 10)));
      this.tip.style.setProperty('--badge-tip-caret-left', `${caretLeft}px`);
    }
    disconnectedCallback() {
      clearTimeout(this.closeTimer);
      clearTimeout(this.hideTimer);
      cancelAnimationFrame(this.openFrame);
      this.isOpen = false;
      this.controller?.abort();
      this.tip?.remove();
      if (this.trigger) this.replaceChildren(...this.trigger.childNodes);
      this.controller = null;
    }
  }
  // Badge controls sit beside media links, never inside an interactive anchor.
  class BadgeMediaLayer extends HTMLElement {
    connectedCallback() {
      this.source = this.previousElementSibling;
      if (!this.source) return;
      const position = () => {
        if (!this.source.isConnected) return;
        Object.assign(this.style, {
          left: `${this.source.offsetLeft}px`, top: `${this.source.offsetTop}px`,
          width: `${this.source.offsetWidth}px`, height: `${this.source.offsetHeight}px`
        });
      };
      this.observer = new ResizeObserver(position);
      this.observer.observe(this.source);
      this.observer.observe(this.parentElement);
      position();
    }
    disconnectedCallback() { this.observer?.disconnect(); }
  }
  customElements.define('badge-explanation', BadgeExplanation);
  customElements.define('badge-media-layer', BadgeMediaLayer);
})();
